import * as React from 'react'
import { createPortal } from 'react-dom'
import { useDate } from '@/modules/drivers/stores/dateStore'
import DatePickerSingle from '@/ui/date/DatePickerPopup'
import { faCalendarSolid, faClose, FaIcon } from '@/lib/fa'

type DatePickerSingleButtonProps = {
  label?: string
  placeholder?: string
  iconAlign?: 'left' | 'right'
  fullWidth?: boolean
  size?: 'small' | 'large'
  highlightSelected?: boolean
  action?: () => void
  clearable?: boolean
  confirmSelection?: boolean
}

const DatePickerSingleButton: React.FC<DatePickerSingleButtonProps> = ({
  placeholder = 'RRRR-MM-DD',
  iconAlign = 'left',
  fullWidth = false,
  size = 'large',
  highlightSelected = false,
  action,
  clearable,
  confirmSelection = true,
}) => {
  const [open, setOpen] = React.useState(false)
  const { single, setSingle } = useDate()

  const containerRef = React.useRef<HTMLDivElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)

  const handleConfirm = (fullDate: string) => {
    setSingle(fullDate)
    setOpen(false)
    if (action) {
      action()
    }
  }

  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
  })

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const willOpen = !open
    if (willOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      })
    }
    setOpen(willOpen)
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setSingle(null)
    setOpen(false)
  }


  React.useLayoutEffect(() => {
    if (!open || !buttonRef.current || !dropdownRef.current) return

    const margin = 8
    const rect = buttonRef.current.getBoundingClientRect()
    const dropdownWidth = dropdownRef.current.offsetWidth
    const dropdownHeight = dropdownRef.current.offsetHeight
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let left = rect.left
    if (left + dropdownWidth > viewportWidth - margin) {
      left = rect.right - dropdownWidth
    }
    if (left < margin) {
      left = margin
    }

    let top = rect.bottom + 4
    if (top + dropdownHeight > viewportHeight - margin) {
      const above = rect.top - dropdownHeight - 4
      if (above > margin) {
        top = above
      }
    }

    setDropdownPosition((prev) =>
      prev.top === top && prev.left === left ? prev : { top, left },
    )
  }, [open])

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={containerRef} className="tw-relative tw-z-50 tw-inline-block">
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        className={`tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded tw-border tw-px-2 tw-py-1 tw-text-base-content hover:tw-bg-[#F5F5F5] ${
          open
            ? 'tw-border-2 tw-border-primary'
            : single && highlightSelected
              ? 'tw-border-primary tw-bg-[#F1F4FF]'
              : 'tw-border-neutral/20 tw-bg-transparent'
        } ${iconAlign === 'right' ? 'tw-flex-row-reverse' : 'tw-flex-row'} ${size === 'small' ? 'tw-py-1' : 'tw-py-2'} ${fullWidth ? 'tw-w-full' : ''}`}
      >
        <FaIcon
          icon={faCalendarSolid}
          className="tw-h-4 tw-w-4 tw-transition-transform tw-duration-200"
        />
        <div className="tw-flex tw-items-center tw-gap-2">
          <p
            className={`tw-text-sm ${single && highlightSelected ? 'tw-text-primary' : ''}`}
          >
            {single || placeholder}
          </p>
          {clearable && single && (
            <FaIcon
              icon={faClose}
              onClick={handleDeleteClick}
              className="tw-h-4 tw-w-4 tw-transition-transform tw-duration-200"
            />
          )}
        </div>
      </button>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="tw-fixed tw-z-[200] tw-shadow-md"
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          >
            <DatePickerSingle
              value={single ?? null}
              submit={handleConfirm}
              confirmSelection={confirmSelection}
            />
          </div>,
          document.body,
        )}
    </div>
  )
}

export default DatePickerSingleButton
